import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom, forkJoin, map, Subscription, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountsService } from '../../accounts.service';
import { CompanyScopeSelection, CompanyScopeSelectorComponent } from '../../company-scope-selector/company-scope-selector.component';
import { Invoice } from '../../invoice/invoice.model';
import { InvoiceDispatch, InvoiceDispatchCandidate, InvoiceDispatchCandidateGroup, InvoiceDispatchEmployee, InvoiceDispatchOverlap, InvoiceDispatchSummary } from './invoice-dispatch.model';
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { LocaleService } from '../../../i18n/locale.service';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';

@Component({
    selector: 'app-invoice-dispatch',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DatePickerModule, DialogModule,
        IftaLabelModule, InputGroupAddonModule, InputGroupModule, InputTextModule, SelectModule,
        TableModule, TagModule, TextareaModule, CompanyScopeSelectorComponent, RouterLink,
        PageHeroComponent, PageShellComponent, PageDataPanelComponent],
    templateUrl: './invoice-dispatch.component.html',
    styleUrls: ['../../finance-list.shared.css', './invoice-dispatch.component.css']
})
export class InvoiceDispatchComponent implements OnInit, OnDestroy {
    private readonly accounts = inject(AccountsService);
    private readonly storage = inject(FirebaseStorageService);
    private readonly permission = inject(PermissionService);
    private readonly locale = inject(LocaleService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly dialogs = inject(DialogService);
    private listSubscription?: Subscription;
    private groupSubscription?: Subscription;
    private candidateSubscription?: Subscription;
    private overlapSubscription?: Subscription;
    private employeeSubscription?: Subscription;
    private searchTimer?: ReturnType<typeof setTimeout>;
    private candidateTimer?: ReturnType<typeof setTimeout>;
    private employeeTimer?: ReturnType<typeof setTimeout>;
    private reissueLoaded = false;

    readonly baseRoute = '/accounts/enterprise/receivables/invoice-dispatches';
    readonly canCreate = this.permission.hasPermission(app_permissions.accounts.invoice.create);
    readonly canUpload = this.permission.hasPermission(app_permissions.accounts.invoice.edit);
    readonly canVoid = this.permission.hasPermission(app_permissions.accounts.invoice.void);
    readonly createMode = this.route.snapshot.routeConfig?.path === 'new';
    detailId?: number;
    detail?: InvoiceDispatch;
    scope?: CompanyScopeSelection;
    rows: InvoiceDispatchSummary[] = [];
    total = 0;
    first = 0;
    pageSize = 20;
    status = '';
    search = '';
    loading = false;
    busy = false;
    error = '';

    groups: InvoiceDispatchCandidateGroup[] = [];
    groupTotal = 0;
    groupFirst = 0;
    groupPageSize = 20;
    groupLoading = false;
    activeGroup?: InvoiceDispatchCandidateGroup;
    candidateRows: InvoiceDispatchCandidate[] = [];
    candidateTotal = 0;
    candidateFirst = 0;
    candidatePageSize = 10;
    candidateSearch = '';
    candidateLoading = false;
    selected = new Map<number, InvoiceDispatchCandidate>();
    overlaps: InvoiceDispatchOverlap[] = [];
    representativeEmployeeId: number | null = null;
    employeeOptions: { label: string; value: number }[] = [];
    employeeLoading = false;
    repeatReason = '';

    proofVisible = false;
    proofFiles: File[] = [];
    receivedDate: Date | null = null;
    recipientName = '';
    recipientTitle = '';
    signedStampedConfirmed = false;
    readonly today = new Date();

    voidVisible = false;
    voidReason = '';

    get statusOptions() {
        return [
            { label: this.t('allStatuses'), value: '' },
            { label: this.t('created'), value: 'CREATED' },
            { label: this.t('acknowledged'), value: 'ACKNOWLEDGED' },
            { label: this.t('voided'), value: 'VOIDED' }
        ];
    }

    get selectedInvoices(): InvoiceDispatchCandidate[] { return [...this.selected.values()]; }
    get selectedTotal(): number { return this.selectedInvoices.reduce((sum, invoice) => sum + invoice.amount, 0); }
    get selectedCurrency(): string { return this.selectedInvoices[0]?.currencyCode ?? ''; }
    get duplicateNumbers(): string[] { return [...new Set(this.overlaps.map(item => item.dispatchNumber))]; }

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (id > 0) { this.detailId = id; this.loadDetail(); }
    }

    ngOnDestroy(): void {
        this.listSubscription?.unsubscribe();
        this.groupSubscription?.unsubscribe();
        this.candidateSubscription?.unsubscribe();
        this.overlapSubscription?.unsubscribe();
        this.employeeSubscription?.unsubscribe();
        if (this.searchTimer) clearTimeout(this.searchTimer);
        if (this.candidateTimer) clearTimeout(this.candidateTimer);
        if (this.employeeTimer) clearTimeout(this.employeeTimer);
    }

    t(key: string): string { return this.locale.translate(`pages.invoiceDispatch.${key}`); }

    onScopeChange(scope: CompanyScopeSelection): void {
        this.scope = scope;
        this.first = 0;
        if (this.createMode) {
            this.candidateSubscription?.unsubscribe();
            this.groupFirst = 0;
            this.activeGroup = undefined;
            this.candidateRows = [];
            this.candidateTotal = 0;
            this.selected.clear();
            this.overlaps = [];
            this.representativeEmployeeId = null;
            this.employeeOptions = [];
            this.loadGroups();
            if (scope.companyId && !scope.readOnly) this.loadEmployees('');
        } else this.loadList();
        const reissue = Number(this.route.snapshot.queryParamMap.get('reissue'));
        if (reissue > 0 && !this.reissueLoaded && !scope.readOnly) {
            this.reissueLoaded = true;
            this.prefillReissue(reissue);
        }
    }

    onListPage(event: TableLazyLoadEvent): void {
        this.first = event.first ?? 0;
        this.pageSize = event.rows ?? 20;
        this.loadList();
    }

    onSearchChange(): void {
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => { this.first = 0; this.loadList(); }, 300);
    }

    loadList(): void {
        if (!this.scope) return;
        this.listSubscription?.unsubscribe();
        this.loading = true;
        this.error = '';
        this.listSubscription = this.accounts.getInvoiceDispatches(this.scope.companyId,
            this.scope.companyGroupId, Math.floor(this.first / this.pageSize), this.pageSize,
            this.status, this.search).subscribe({
            next: result => { this.rows = result.items; this.total = result.count; this.loading = false; },
            error: err => { this.error = this.failure(err); this.loading = false; }
        });
    }

    loadDetail(): void {
        if (!this.detailId) return;
        this.loading = true;
        this.error = '';
        this.accounts.getInvoiceDispatch(this.detailId).subscribe({
            next: detail => { this.detail = detail; this.loading = false; },
            error: err => { this.error = this.failure(err); this.loading = false; }
        });
    }

    openCreate(): void {
        if (!this.scope?.companyId || this.scope.readOnly || !this.canCreate) return;
        this.router.navigate([this.baseRoute, 'new'], { queryParams: { companyId: this.scope.companyId } });
    }

    onEmployeeFilter(searchText: string): void {
        if (this.employeeTimer) clearTimeout(this.employeeTimer);
        this.employeeTimer = setTimeout(() => this.loadEmployees(searchText), 300);
    }

    loadEmployees(searchText: string): void {
        if (!this.scope?.companyId) return;
        this.employeeSubscription?.unsubscribe();
        this.employeeLoading = true;
        this.employeeSubscription = this.accounts.getInvoiceDispatchEmployees(this.scope.companyId, searchText).subscribe({
            next: employees => {
                const selectedOption = this.employeeOptions.find(option => option.value === this.representativeEmployeeId);
                const options = employees.map((employee: InvoiceDispatchEmployee) => ({
                    label: `${employee.displayName}${employee.employeeNumber ? ' · ' + employee.employeeNumber : ''}`,
                    value: employee.employeeId
                }));
                this.employeeOptions = selectedOption && !options.some(option => option.value === selectedOption.value)
                    ? [selectedOption, ...options] : options;
                this.employeeLoading = false;
            },
            error: err => { this.error = this.failure(err); this.employeeLoading = false; }
        });
    }

    onCandidateSearchChange(): void {
        if (this.candidateTimer) clearTimeout(this.candidateTimer);
        this.candidateTimer = setTimeout(() => {
            this.groupFirst = 0;
            this.candidateFirst = 0;
            this.loadGroups();
            this.loadCandidates();
        }, 300);
    }

    onGroupPage(event: TableLazyLoadEvent): void {
        this.groupFirst = event.first ?? 0;
        this.groupPageSize = event.rows ?? 20;
        this.loadGroups();
    }

    loadGroups(): void {
        if (!this.scope?.companyId || this.scope.readOnly) return;
        this.groupSubscription?.unsubscribe();
        this.groupLoading = true;
        this.groupSubscription = this.accounts.getInvoiceDispatchCandidateGroups(this.scope.companyId,
            Math.floor(this.groupFirst / this.groupPageSize), this.groupPageSize, this.candidateSearch).subscribe({
            next: result => { this.groups = result.items; this.groupTotal = result.count; this.groupLoading = false; },
            error: err => { this.error = this.failure(err); this.groupLoading = false; }
        });
    }

    openGroup(group: InvoiceDispatchCandidateGroup): void {
        this.activeGroup = group;
        this.candidateFirst = 0;
        this.loadCandidates();
    }

    onCandidatePage(event: TableLazyLoadEvent): void {
        this.candidateFirst = event.first ?? 0;
        this.candidatePageSize = event.rows ?? 10;
        this.loadCandidates();
    }

    loadCandidates(): void {
        if (!this.scope?.companyId || !this.activeGroup) return;
        this.candidateSubscription?.unsubscribe();
        this.candidateLoading = true;
        this.candidateSubscription = this.accounts.getInvoiceDispatchCandidates(this.scope.companyId,
            this.activeGroup.recipientType, this.activeGroup.recipientId,
            Math.floor(this.candidateFirst / this.candidatePageSize), this.candidatePageSize,
            this.candidateSearch).subscribe({
            next: result => { this.candidateRows = result.items; this.candidateTotal = result.count; this.candidateLoading = false; },
            error: err => { this.error = this.failure(err); this.candidateLoading = false; }
        });
    }

    isSelected(invoice: InvoiceDispatchCandidate): boolean { return this.selected.has(invoice.invoiceId); }

    toggle(invoice: InvoiceDispatchCandidate, checked: boolean): void {
        const id = invoice.invoiceId;
        if (checked) {
            const first = this.selectedInvoices[0];
            if (first && (this.partyKey(first) !== this.partyKey(invoice)
                || first.currencyCode !== invoice.currencyCode)) {
                this.error = this.t('oneClientCurrency');
                return;
            }
            this.selected.set(id, invoice);
        } else this.selected.delete(id);
        this.error = '';
        this.loadOverlaps();
    }

    loadOverlaps(): void {
        this.overlapSubscription?.unsubscribe();
        if (!this.scope?.companyId || !this.selected.size) { this.overlaps = []; return; }
        this.overlapSubscription = this.accounts.getInvoiceDispatchOverlaps(
            this.scope.companyId, [...this.selected.keys()]).subscribe({
            next: rows => this.overlaps = rows,
            error: err => this.error = this.failure(err)
        });
    }

    create(): void {
        if (!this.scope?.companyId || !this.selected.size || !this.representativeEmployeeId
            || (this.overlaps.length && !this.repeatReason.trim())) {
            this.error = this.t('completeCreate'); return;
        }
        this.busy = true;
        this.error = '';
        this.accounts.createInvoiceDispatch({ companyId: this.scope.companyId,
            invoiceIds: [...this.selected.keys()], representativeEmployeeId: this.representativeEmployeeId,
            repeatReason: this.repeatReason.trim() || undefined }).subscribe({
            next: created => { this.busy = false; this.router.navigate([this.baseRoute, 'view', created.id]); },
            error: err => { this.error = this.failure(err); this.busy = false; }
        });
    }

    openProof(): void {
        this.proofFiles = [];
        this.receivedDate = null;
        this.recipientName = '';
        this.recipientTitle = '';
        this.signedStampedConfirmed = false;
        this.proofVisible = true;
    }

    onProofFiles(event: Event): void {
        this.proofFiles = Array.from((event.target as HTMLInputElement).files ?? []);
    }

    uploadProof(): void {
        if (!this.detail || !this.receivedDate || !this.proofFiles.length || !this.signedStampedConfirmed) {
            this.error = this.t('completeProof'); return;
        }
        const types = this.proofFiles.map(file => this.proofContentType(file));
        if (this.proofFiles.length > 10 || this.proofFiles.some(file => !file.size || file.size > 20 * 1024 * 1024)
            || types.some(type => !type)) {
            this.error = this.t('invalidProofFiles'); return;
        }
        this.busy = true;
        this.error = '';
        const dispatch = this.detail;
        forkJoin(this.proofFiles.map((file, index) => this.storage.uploadFileAndGetStorageUri(file,
            `accounting/invoice-dispatch-staging/${dispatch.owningCompanyId}/${dispatch.id}/${crypto.randomUUID()}`,
            types[index]!).pipe(map(url => ({ url, filename: file.name }))))).pipe(
            switchMap(files => this.accounts.recordInvoiceDispatchProof(dispatch.id, files,
                this.localDate(this.receivedDate!), this.recipientName, this.recipientTitle))
        ).subscribe({
            next: detail => { this.detail = detail; this.proofVisible = false; this.busy = false; },
            error: err => { this.error = this.failure(err); this.busy = false; }
        });
    }

    voidDispatch(): void {
        if (!this.detailId || !this.voidReason.trim()) { this.error = this.t('voidReasonRequired'); return; }
        this.busy = true;
        this.error = '';
        this.accounts.voidInvoiceDispatch(this.detailId, this.voidReason.trim()).subscribe({
            next: detail => { this.detail = detail; this.voidVisible = false; this.busy = false; },
            error: err => { this.error = this.failure(err); this.busy = false; }
        });
    }

    downloadOriginal(): void {
        if (!this.detail) return;
        this.accounts.downloadInvoiceDispatch(this.detail.id).subscribe({
            next: blob => this.saveBlob(blob, `invoice-dispatch-${this.detail?.dispatchNumber.replaceAll('/', '-')}.pdf`),
            error: err => this.error = this.failure(err)
        });
    }

    async viewOriginal(): Promise<void> {
        if (!this.detail || this.busy) return;
        this.busy = true;
        try {
            const pdf = await firstValueFrom(this.accounts.downloadInvoiceDispatch(this.detail.id));
            const { FinancePdfViewerComponent } = await import('../../finance-pdf-viewer.component');
            const data: { pdf?: Blob; reference: string; variant: 'ORIGINAL' } = {
                pdf, reference: this.detail.dispatchNumber, variant: 'ORIGINAL'
            };
            this.dialogs.open(FinancePdfViewerComponent, {
                header: this.t('viewPdf'), modal: true, closable: true, closeOnEscape: true,
                draggable: false, resizable: false, width: 'min(1100px, 96vw)',
                breakpoints: { '640px': '100vw' },
                contentStyle: { padding: '0', overflow: 'hidden' }, data
            })?.onClose.subscribe(() => { data.pdf = undefined; });
        } catch (err: any) {
            this.error = this.failure(err);
        } finally {
            this.busy = false;
        }
    }

    downloadProof(proofId: number, filename: string): void {
        if (!this.detailId) return;
        this.accounts.downloadInvoiceDispatchProof(this.detailId, proofId).subscribe({
            next: blob => this.saveBlob(blob, filename), error: err => this.error = this.failure(err)
        });
    }

    reissue(): void {
        if (!this.detail) return;
        this.router.navigate([this.baseRoute, 'new'],
            { queryParams: { companyId: this.detail.owningCompanyId, reissue: this.detail.id } });
    }

    private prefillReissue(id: number): void {
        if (!this.scope?.companyId) return;
        this.accounts.getInvoiceDispatch(id).subscribe({
            next: dispatch => {
                if (dispatch.owningCompanyId !== this.scope?.companyId) { this.error = this.t('selectOwningCompany'); return; }
                forkJoin(dispatch.invoices.map(line => this.accounts.getInvoiceById(line.invoiceId))).subscribe({
                    next: invoices => {
                        invoices.forEach(invoice => {
                            const candidate = this.fromInvoice(invoice);
                            if (candidate) this.selected.set(candidate.invoiceId, candidate);
                        });
                        const first = this.selectedInvoices[0];
                        if (first) this.openGroup({ recipientType: first.recipientType,
                            recipientId: first.recipientId, recipientName: first.recipientName, invoiceCount: 0 });
                        this.loadOverlaps();
                    },
                    error: err => this.error = this.failure(err)
                });
            }, error: err => this.error = this.failure(err)
        });
    }

    private partyKey(invoice: InvoiceDispatchCandidate): string {
        return `${invoice.recipientType}:${invoice.recipientId}`;
    }

    private fromInvoice(invoice: Invoice): InvoiceDispatchCandidate | null {
        if (!invoice.invoiceId || !invoice.invoiceNumber || invoice.postingStatus !== 'POSTED'
            || ['CANCELLED', 'VOIDED', 'REVERSED'].includes(invoice.documentStatus ?? '')
            || invoice.cancelledAt || invoice.voidedAt || invoice.reversedAt) return null;
        const recipientType = invoice.crmClientId ? 'CRM' : invoice.mainParty?.id?.entityType;
        const recipientId = invoice.crmClientId || invoice.mainParty?.id?.entityId;
        if (!recipientType || !recipientId || !invoice.country?.currencyCode) return null;
        return { invoiceId: invoice.invoiceId, invoiceNumber: invoice.invoiceNumber,
            invoiceDate: String(invoice.invoiceCreationDate ?? ''), recipientType, recipientId,
            recipientName: invoice.crmDisplayName || invoice.mainParty?.entityName || '',
            currencyCode: invoice.country.currencyCode, amount: invoice.totalAmount ?? 0,
            netAmount: invoice.totalAmountExclTax ?? 0, taxAmount: invoice.totalTax ?? 0,
            dueDate: String(invoice.dueDate ?? ''),
            jobReference: invoice.sourceReference?.trim() || invoice.externalReferenceNumber?.trim(),
            sourceReference: invoice.sourceReference, externalReferenceNumber: invoice.externalReferenceNumber,
            deliveryReference: invoice.deliveryReference, invoiceType: invoice.invoiceType,
            paymentTerms: invoice.paymentTerms, zatcaStatus: invoice.zatcaStatus,
            earlierDispatchCount: 1 };
    }

    private localDate(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    private proofContentType(file: File): string | null {
        const suffix = file.name.toLowerCase().split('.').pop();
        const type = suffix === 'pdf' ? 'application/pdf' : suffix === 'png' ? 'image/png'
            : suffix === 'jpg' || suffix === 'jpeg' ? 'image/jpeg' : null;
        return type && (!file.type || file.type === 'application/octet-stream' || file.type === type)
            ? type : null;
    }

    private saveBlob(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = filename; link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    private failure(err: { error?: { detail?: string; message?: string }; message?: string }): string {
        return err.error?.detail || err.error?.message || err.message || this.t('loadFailed');
    }
}
